   import React, { useState, useEffect, useMemo } from 'react';
 import { Line } from 'react-chartjs-2';
 import DatePicker from "react-datepicker";
 import "react-datepicker/dist/react-datepicker.css";
 import * as XLSX from 'xlsx';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

import { useNavigate } from 'react-router-dom';
import Select from "react-select";
import api from '../api/api';
import '../App.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Control-chart constants for sample size n = 10
const CONTROL_CHART_CONSTANTS = {
  10: { A2: 0.308, D3: 0.223, D4: 1.777 }
};

export default function Dashboard() {
  const navigate = useNavigate();

  // Filters & UI state
  const [suppliers, setSuppliers] = useState([]);
  const [partNos, setPartNos] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedPartNo, setSelectedPartNo] = useState(null);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [allData, setAllData] = useState([]); // raw processed rows
  const [table1Data, setTable1Data] = useState([]); // summary data
  const [uniqueParameters, setUniqueParameters] = useState([]);
  const [selectedParameter, setSelectedParameter] = useState("");

  // UI Tabs
  const [activeTab, setActiveTab] = useState("Report");

  // Load supplier & part options on mount
  useEffect(() => {
    const userName = sessionStorage.getItem("userName");
    const databaseKey = sessionStorage.getItem("databaseKey");
    if (!userName || !databaseKey) return;

    api.InspReportSupplier_API(userName, databaseKey)
      .then(res => {
        const supTable = res?.InspReportSupplier_APIjs?.Table || [];
        const partTable = res?.InspReportSupplier_APIjs?.Table1 || [];

        setSuppliers(supTable.map(s => ({ value: s.Vid, label: s.Vname })));
        setPartNos(partTable.map(p => ({ value: p.itemid, label: p.Partno })));
      })
      .catch(err => {
        console.error("Supplier/Part fetch error:", err);
      });
  }, []);

  // Submit / load inspection data
  const handleSubmit = async () => {
    if (!selectedSupplier || !selectedPartNo) {
      alert("Please select supplier & part no!");
      return;
    }

    const userName = sessionStorage.getItem("userName");
    const databaseKey = sessionStorage.getItem("databaseKey");
    if (!userName || !databaseKey) {
      alert("Missing user/database credentials in sessionStorage.");
      return;
    }

    // Format dates as YYYY-MM-DD strings
    const fDate = startDate ? startDate.toISOString().split("T")[0] : null;
    const tDate = endDate ? endDate.toISOString().split("T")[0] : null;
    setActiveTab("Graph");

    try {
      const res = await api.GetInspectionDatas_API(
        selectedSupplier.value,
        selectedPartNo.value,
        fDate,
        tDate,
        userName,
        databaseKey
      );

      // API returns Table in GetInspectionDatas_APIjs.Table
      const data = res?.GetInspectionDatas_APIjs?.Table || [];
      const table1 = res?.GetInspectionDatas_APIjs?.Table1 || [];

      setTable1Data(table1);

      // Normalize & process
      const processed = data.map(d => ({
        ...d,
        // convert incoming date strings to JS Date for UI if possible
        date: d.date ? new Date(d.date) : null,
        lower_limit: d.lower_limit !== null && d.lower_limit !== undefined ? Number(d.lower_limit) : null,
        upper_limit: d.upper_limit !== null && d.upper_limit !== undefined ? Number(d.upper_limit) : null,
        x_bar: d.x_bar === null || d.x_bar === undefined ? null : Number(d.x_bar),
        r_value: d.r_value === null || d.r_value === undefined ? null : Number(d.r_value),
        // collect observations (Obs1..Obs10) into array, filter blanks/null
        observations: [
          d.Obs1, d.Obs2, d.Obs3, d.Obs4, d.Obs5,
          d.Obs6, d.Obs7, d.Obs8, d.Obs9, d.Obs10
        ].filter(v => v !== null && v !== undefined && String(v).trim() !== "")
      }));

      setAllData(processed);

      // populate parameter dropdown
      const params = [...new Set(processed.map(p => (p.parameter || "").toString().trim()).filter(x => x !== ""))];
      setUniqueParameters(params);
      if (params.length > 0) setSelectedParameter(prev => prev || params[0]);

    } catch (e) {
      console.error("API Error:", e);
      alert("Failed to load inspection data! See console for details.");
    }
  };

  // Filtered data for charts / table: by parameter and date range
  const filteredData = useMemo(() => {
    if (!selectedParameter) return [];
    const fromISO = startDate ? startDate.toISOString().split("T")[0] : null;
    const toISO = endDate ? endDate.toISOString().split("T")[0] : null;

    return allData
      .filter(d => {
        // parameter match
        if ((d.parameter || "").toString().trim() !== selectedParameter) return false;

        // date bounds (compare by YYYY-MM-DD string if Date present)
        if (fromISO && d.date) {
          const dd = d.date.toISOString().split("T")[0];
          if (dd < fromISO) return false;
        }
        if (toISO && d.date) {
          const dd = d.date.toISOString().split("T")[0];
          if (dd > toISO) return false;
        }
        return true;
      })
      // sort same as your uploaded file: by file if provided (fallback to date)
      .sort((a, b) => {
        if (a.file && b.file) return String(a.file).localeCompare(String(b.file));
        if (a.date && b.date) return a.date - b.date;
        return 0;
      });
  }, [allData, selectedParameter, startDate, endDate]);

  // summary & chart data (X-bar and R chart)
  const chartData = useMemo(() => {
    if (!Array.isArray(filteredData) || filteredData.length === 0) {
      return { xBar: null, rChart: null, stats: null };
    }

    // Only include samples that have numeric x_bar / r_value
    const validXBars = filteredData.map(d => d.x_bar).filter(v => v !== null && v !== undefined && !Number.isNaN(v));
    const validR = filteredData.map(d => d.r_value).filter(v => v !== null && v !== undefined && !Number.isNaN(v));

    if (validXBars.length === 0 || validR.length === 0) {
      return { xBar: null, rChart: null, stats: null };
    }

    // compute averages
    const X_double_bar = validXBars.reduce((a, b) => a + b, 0) / validXBars.length;
    const R_bar = validR.reduce((a, b) => a + b, 0) / validR.length;

    // use constants for n = 10
    const n = 10;
    const consts = CONTROL_CHART_CONSTANTS[n];
    if (!consts) return { xBar: null, rChart: null, stats: null };

    const UCL_X = X_double_bar + consts.A2 * R_bar;
    const LCL_X = X_double_bar - consts.A2 * R_bar;
    const CL_X = X_double_bar;

    const UCL_R = consts.D4 * R_bar;
    const LCL_R = consts.D3 * R_bar;
    const CL_R = R_bar;

    // specification limits from first row (if present)
    const first = filteredData[0];
    const LSL = first?.lower_limit ?? null;
    const USL = first?.upper_limit ?? null;

    const labels = filteredData.map((_, i) => `Sample ${i + 1}`);
    const xBars = filteredData.map(d => (d.x_bar !== null && d.x_bar !== undefined ? d.x_bar : null));
    const rVals = filteredData.map(d => (d.r_value !== null && d.r_value !== undefined ? d.r_value : null));

    // Build datasets
    const xBarData = {
      labels,
      datasets: [
        {
          label: 'X̄ (Sample mean)',
          data: xBars,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.1,
          spanGaps: true,
          pointRadius: 3
        },
        {
          label: 'UCL (X-bar)',
          data: labels.map(() => Number(UCL_X.toFixed(6))),
          borderColor: 'rgb(255, 99, 132)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
        {
          label: 'CL (X-bar)',
          data: labels.map(() => Number(CL_X.toFixed(6))),
          borderColor: 'rgb(54, 162, 235)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
        {
          label: 'LCL (X-bar)',
          data: labels.map(() => Number(LCL_X.toFixed(6))),
          borderColor: 'rgb(255, 99, 132)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
        ...(LSL !== null ? [{
          label: 'LSL',
          data: labels.map(() => LSL),
          borderColor: 'rgb(255, 159, 64)',
          borderDash: [10, 10],
          pointRadius: 0,
          fill: false
        }] : []),
        ...(USL !== null ? [{
          label: 'USL',
          data: labels.map(() => USL),
          borderColor: 'rgb(255, 159, 64)',
          borderDash: [10, 10],
          pointRadius: 0,
          fill: false
        }] : [])
      ]
    };

    const rChartData = {
      labels,
      datasets: [
        {
          label: 'R',
          data: rVals,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.1,
          spanGaps: true,
          pointRadius: 3
        },
        {
          label: 'UCL (R)',
          data: labels.map(() => Number(UCL_R.toFixed(6))),
          borderColor: 'rgb(255, 99, 132)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
        {
          label: 'CL (R)',
          data: labels.map(() => Number(CL_R.toFixed(6))),
          borderColor: 'rgb(54, 162, 235)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
        {
          label: 'LCL (R)',
          data: labels.map(() => Number(LCL_R.toFixed(6))),
          borderColor: 'rgb(255, 99, 132)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
      ]
    };

    const chartTitle = `${selectedParameter || ''} (${startDate ? startDate.toISOString().split("T")[0] : 'N/A'} to ${endDate ? endDate.toISOString().split("T")[0] : 'N/A'})`;

    const xBarOptions = {
      responsive: true,
      plugins: {
        legend: { position: 'top' },
        title: { display: true, text: `X-Bar Chart for ${chartTitle}` }
      },
      interaction: { mode: 'index', intersect: false },
      scales: { y: { beginAtZero: false } }
    };

    const rChartOptions = {
      responsive: true,
      plugins: {
        legend: { position: 'top' },
        title: { display: true, text: `R Chart for ${chartTitle}` }
      },
      interaction: { mode: 'index', intersect: false },
      scales: { y: { beginAtZero: true } }
    };

    const stats = {
      samples: filteredData.length,
      LSL, USL,
      CL_X: CL_X.toFixed(4),
      UCL_X: UCL_X.toFixed(4),
      LCL_X: LCL_X.toFixed(4),
      CL_R: CL_R.toFixed(4),
      UCL_R: UCL_R.toFixed(4),
      LCL_R: LCL_R.toFixed(4)
    };

    return {
      xBar: { data: xBarData, options: xBarOptions },
      rChart: { data: rChartData, options: rChartOptions },
      stats
    };

  }, [filteredData, selectedParameter, startDate, endDate]);

  // Small presentational subcomponents for tab content
  const ReportTab = () => (
  <div className="p-4 bg-white rounded shadow">
    <h3 className="font-semibold mb-3 text-gray-800">Report Filters</h3>

    <div className="grid grid-cols-1 md:grid-cols-4 gap-5">

     <div className="flex flex-col gap-1">
  <label className="text-gray-600 text-xs font-medium">Supplier</label>

  <Select
    options={suppliers}
    value={selectedSupplier}
    onChange={setSelectedSupplier}
    placeholder="Select Supplier"
    className="text-xs"
    classNamePrefix="supplier-select"
    styles={{
      control: (base) => ({
        ...base,
        minHeight: "34px",
        borderColor: "#d1d5db",
        fontSize: "12px",
        textAlign: "left",
      }),
      singleValue: (base) => ({
        ...base,
        textAlign: "left",
        fontSize: "12px",
      }),
      menu: (base) => ({
        ...base,
        textAlign: "left",
        fontSize: "12px",
      }),
      option: (base) => ({
        ...base,
        textAlign: "left",
        fontSize: "12px",
      }),
    }}
  />
</div>


      <div className="flex flex-col gap-1">
  <label className="text-gray-600 text-xs font-medium">Part No</label>

  <Select
    options={partNos}
    value={selectedPartNo}
    onChange={setSelectedPartNo}
    placeholder="Select Part No"
    className="text-xs"
    classNamePrefix="partno-select"
    styles={{
      control: (base) => ({
        ...base,
        minHeight: "34px",
        borderColor: "#d1d5db",
        fontSize: "12px",
        textAlign: "left",
      }),
      singleValue: (base) => ({
        ...base,
        textAlign: "left",
        fontSize: "12px",
      }),
      menu: (base) => ({
        ...base,
        textAlign: "left",
        fontSize: "12px",
      }),
      option: (base) => ({
        ...base,
        textAlign: "left",
        fontSize: "12px",
      }),
      placeholder: (base) => ({
        ...base,
        textAlign: "left",
        fontSize: "12px",
      }),
    }}
  />
</div>


      <div className="flex flex-col gap-1">
        <label className="text-gray-600 text-xs font-medium">From Date</label>
        <DatePicker
          selected={startDate}
          onChange={(date) => setStartDate(date)}
          dateFormat="dd-MM-yyyy"
          className="border border-gray-300 rounded-lg p-2 h-[34px] w-full text-xs"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-gray-600 text-xs font-medium">To Date</label>
        <DatePicker
          selected={endDate}
          onChange={(date) => setEndDate(date)}
          dateFormat="dd-MM-yyyy"
          className="border border-gray-300 rounded-lg p-2 h-[34px] w-full text-xs"
        />
      </div>

    </div>

    <div className="mt-4 text-right">
      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-4 py-2 rounded text-xs hover:bg-blue-700"
      >
        Submit
      </button>
    </div>
  </div>
);


  const GraphTab = () => (
    <div>
       

      {filteredData.length > 0 && chartData.stats ? (
        <div className="content-wrapper lg:flex-row gap-2 mt-4 w-full">
          <div className="stats-summary bg-white shadow-md p-4 pl-2 rounded-lg w-full lg:w-2/3">
            <h2 className="font-bold text-lg mb-2">Summary Statistics</h2>
            <p><strong>Parameter:</strong> {selectedParameter}</p>
            <p><strong>Date Range:</strong> {startDate ? startDate.toISOString().split("T")[0] : '-'} to {endDate ? endDate.toISOString().split("T")[0] : '-'}</p>
            <p><strong>Number of Samples:</strong> {chartData.stats.samples}</p>
            <p><strong>Specification Limit (LSL):</strong> {chartData.stats.LSL ?? 'N/A'}</p>
            <p><strong>Specification Limit (USL):</strong> {chartData.stats.USL ?? 'N/A'}</p>

            <hr className="my-2" />
            <h3 className="font-bold">X-Bar Chart Stats</h3>
            <p><strong>X-double-bar (CL):</strong> {chartData.stats.CL_X}</p>
            <p><strong>UCL (X-bar):</strong> {chartData.stats.UCL_X}</p>
            <p><strong>LCL (X-bar):</strong> {chartData.stats.LCL_X}</p>

            <hr className="my-2" />
            <h3 className="font-bold">R Chart Stats</h3>
            <p><strong>R-bar (CL):</strong> {chartData.stats.CL_R}</p>
            <p><strong>UCL (R):</strong> {chartData.stats.UCL_R}</p>
            <p><strong>LCL (R):</strong> {chartData.stats.LCL_R}</p>
          </div>

          <div className="charts-container w-full lg:w-2/3">
            {chartData.xBar && (
              <div className="bg-white shadow-md p-3 rounded mb-6">
                <Line data={chartData.xBar.data} options={chartData.xBar.options} />
              </div>
            )}

            {chartData.rChart && (
              <div className="bg-white shadow-md p-3 rounded mb-6">
                <Line data={chartData.rChart.data} options={chartData.rChart.options} />
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="mt-4">No data available for the selected parameter and date range</p>
      )}
      {filteredData.length > 0 && chartData.stats ? (
        <div className="overflow-x-auto bg-white p-4 shadow rounded">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="p-2 border text-left">Sample(File)</th>
                <th className="p-2 border text-left">X-bar (X̄)</th>
               <th className="p-2 border text-left">Range (R)</th>
                <th className="p-2 border text-left">Observations (n=10)</th>
                
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, i) => (
                <tr key={i} className="border-t">
                  <td className="p-2 border text-left">Sample {i + 1}{item.file ? ` (${item.file})` : ''}</td>
                  <td className="p-2 border">{item.x_bar != null ? Number(item.x_bar).toFixed(4) : "-"}</td>
                  <td className="p-2 border">{item.r_value != null ? Number(item.r_value).toFixed(4) : "-"}</td>
                  <td className="p-2 border text-left">{(item.observations || []).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : filteredData.length === 0 ? (
        <p>No detailed data available. Load data via Report tab.</p>
      ) : null}
    </div>
  );
  
const DetailsTab = ({ VId, Itemid, fromDate, toDate, userName, databaseKey, supplierName, partNo, table1Data }) => {
  const [tableData, setTableData] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsData, setDetailsData] = useState([]);
  const [filters, setFilters] = useState({
    SlNo: '',
    GIpNo: '',
    GIpdate: '',
    QcRefNo: '',
    QcRefdate: '',
    Dcno: '',
    Dcdate: '',
    AcceptQty: '',
    QCRejQty: '',
    GrnBy: '',
    QcByName: '',
    Partno: '',
    Partdesc: '',
    Uom: ''
  });

  useEffect(() => {
    const converted = table1Data.map(item => ({
      Vid: item.Vid ?? "",
      Vname: item.Vname ?? "",
      partno: item.partno ?? "",
      partdesc: item.partdesc ?? "",
      uom: item.uom ?? "",
      incomingReceipts: item["Incoming Receipts"] ?? 0,
      incomingQty: item["Incoming Qty"] ?? 0,
    }));
    setTableData(converted);
  }, [table1Data]);

  const handleDetailsClick = async (row) => {
    try {
      const res = await api.InspectionDetailsRpt_API(row.Vid, userName, databaseKey);
      const data = res?.InspectionDetailsRpt_APIjs?.Table || [];
      const enrichedData = data.map(d => ({ ...d, Partno: row.partno, Partdesc: row.partdesc, Uom: row.uom }));
      setDetailsData(enrichedData);
      setFilters({
        SlNo: '',
        GIpNo: '',
        GIpdate: '',
        QcRefNo: '',
        QcRefdate: '',
        Dcno: '',
        Dcdate: '',
        AcceptQty: '',
        QCRejQty: '',
        GrnBy: '',
        QcByName: '',
        Partno: '',
        Partdesc: '',
        Uom: ''
      });
      setShowDetailsModal(true);
    } catch (error) {
      console.error("Error fetching details:", error);
      alert("Failed to load details.");
    }
  };

  const filteredDetailsData = detailsData.filter(d => {
    return Object.keys(filters).every(key => {
      const filterValue = filters[key].toLowerCase();
      const cellValue = (d[key] || '').toString().toLowerCase();
      return cellValue.includes(filterValue);
    });
  });

  const handleViewClick = (d) => {
    window.open(`/editqc/${d.GipId}?view=true`, '_blank', 'width=1200,height=800,scrollbars=no,resizable=yes');
  };

  const handleExportExcel = () => {
    const exportData = filteredDetailsData.map((d, i) => {
      const { GipId, ...rest } = d;
      return { SlNo: i + 1, ...rest };
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inspection Details");
    XLSX.writeFile(wb, "InspectionDetails.xlsx");
  };

  return (
    <div className="mt-4">
      <div className="overflow-x-auto border rounded-lg shadow-sm">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Slno</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Vname</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Part No</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Part Description</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">UOM</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Incoming Receipts</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Incoming Qty</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Details</th>
            </tr>
          </thead>

          <tbody>
            {tableData.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-4 text-gray-500 text-sm">
                  No records found
                </td>
              </tr>
            ) : (
              tableData.map((row, idx) => (
               <tr key={row.Vid + "-" + idx} className="border-t hover:bg-gray-50">
  <td className="px-4 py-2 text-sm text-left">{idx + 1}</td>
  <td className="px-4 py-2 text-sm text-left">{row.Vname}</td>
  <td className="px-4 py-2 text-sm text-left">{row.partno}</td>
  <td className="px-4 py-2 text-sm text-left">{row.partdesc}</td>
  <td className="px-4 py-2 text-sm text-left">{row.uom}</td>
  <td className="px-4 py-2 text-sm text-left">{row.incomingReceipts}</td>
  <td className="px-4 py-2 text-sm text-left">
    {Number(row.incomingQty).toFixed(3)}
  </td>
  <td className="px-4 py-2 text-left">
    <button
      className="bg-blue-600 text-white text-xs px-3 py-1 rounded hover:bg-blue-700"
      onClick={() => handleDetailsClick(row)}
    >
      Details
    </button>
  </td>
</tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal for inspection details */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex justify-center items-center z-50">
          <style dangerouslySetInnerHTML={{__html: `.modal-scroll::-webkit-scrollbar { display: none; }`}} />
          <div className="bg-white rounded-lg p-4 w-full max-h-[95vh] overflow-y-auto modal-scroll shadow-2xl border border-gray-200" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                >
                  Back
                </button>
              </div>
              <h2 className="font-bold text-lg text-center flex-1">Inspection Details</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleExportExcel}
                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                >
                  Export Excel
                </button>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-red-500 font-bold text-lg"
                >

                </button>
              </div>
            </div>

            {/* {(supplierName || partNo) && (
              <div className="mb-4">
                <h3 className="font-semibold text-lg">
                  {supplierName && `Supplier: ${supplierName}`}
                  {supplierName && partNo && ' | '}
                  {partNo && `Part No: ${partNo}`}
                </h3>
              </div>
            )} */}

            <table className="min-w-full border-collapse border">
              <thead>
                <tr className="bg-gray-50">
                  {["SlNo","GIpNo","GIpdate","QcRefNo","QcRefdate","Dcno","Dcdate","Partno","Partdesc","Uom","Mcno","AcceptQty","QCRejQty","GrnBy","QcByName","View"].map(
                    (col, index) => (
                      <th key={`filter-${col}`} className="border px-2 py-1" style={{
                        minWidth: col === 'SlNo' ? '50px' :
                                  col === 'GIpNo' ? '80px' :
                                  col === 'GIpdate' || col === 'QcRefdate' || col === 'Dcdate' ? '90px' :
                                  col === 'QcRefNo' || col === 'Dcno' ? '100px' :
                                  col === 'Partno' || col === 'Partdesc' ? '120px' :
                                  col === 'Uom' || col === 'Mcno' || col === 'AcceptQty' || col === 'QCRejQty' ? '50px' :
                                  col === 'GrnBy' || col === 'QcByName' ? '100px' :
                                  col === 'View' ? '70px' : '100px'
                      }}>
                        {col !== 'View' && col !== 'SlNo' ? (
                          <input
                            type="text"
                            placeholder={`Filter ${col}`}
                            value={filters[col] || ''}
                            onChange={(e) => setFilters(prev => ({ ...prev, [col]: e.target.value }))}
                            className="w-full text-xs border border-gray-300 rounded px-1 py-0.5"
                          />
                        ) : ''}
                      </th>
                    )
                  )}
                </tr>
                <tr className="bg-gray-100 text-xs">
                  {["SlNo","GIpNo","GIpdate","QcRefNo","QcRefdate","Dcno","Dcdate","Partno","Partdesc","Uom","Mcno","AcceptQty","QCRejQty","GrnBy","QcByName","View"].map(
                    (col) => (
                      <th key={col} className="border px-2 py-1" style={{
                        minWidth: col === 'SlNo' ? '50px' :
                                  col === 'GIpNo' ? '80px' :
                                  col === 'GIpdate' || col === 'QcRefdate' || col === 'Dcdate' ? '90px' :
                                  col === 'QcRefNo' || col === 'Dcno' ? '100px' :
                                  col === 'Partno' || col === 'Partdesc' ? '120px' :
                                  col === 'Uom' || col === 'Mcno' || col === 'AcceptQty' || col === 'QCRejQty' ? '50px' :
                                  col === 'GrnBy' || col === 'QcByName' ? '100px' :
                                  col === 'View' ? '70px' : '100px'
                      }}>{col}</th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredDetailsData.length === 0 ? (
                  <tr>
                    <td colSpan={15} className="text-center py-4 text-gray-500 text-sm">
                      No details available
                    </td>
                  </tr>
                ) : (
                  filteredDetailsData.map((d, i) => (
                    <tr key={i} className="border-t hover:bg-gray-50 text-xs">
                      <td className="border px-2 py-1">{i + 1}</td>
                      <td className="border px-2 py-1">{d.GIpNo}</td>
                      <td className="border px-2 py-1">{d.GIpdate ? (() => { const date = new Date(d.GIpdate); return `${date.getDate().toString().padStart(2,'0')}-${(date.getMonth()+1).toString().padStart(2,'0')}-${date.getFullYear()}`; })() : '-'}</td>
                      <td className="border px-2 py-1">{d.QcRefNo}</td>
                      <td className="border px-2 py-1">{d.QcRefdate ? (() => { const date = new Date(d.QcRefdate); return `${date.getDate().toString().padStart(2,'0')}-${(date.getMonth()+1).toString().padStart(2,'0')}-${date.getFullYear()}`; })() : '-'}</td>
                      <td className="border px-2 py-1">{d.Dcno}</td>
                      <td className="border px-2 py-1">{d.Dcdate ? (() => { const date = new Date(d.Dcdate); return `${date.getDate().toString().padStart(2,'0')}-${(date.getMonth()+1).toString().padStart(2,'0')}-${date.getFullYear()}`; })() : '-'}</td>
                      <td className="border px-2 py-1">{d.Partno}</td>
                      <td className="border px-2 py-1">{d.Partdesc}</td>
                      <td className="border px-2 py-1">{d.Uom}</td>
                      <td className="border px-2 py-1">{d.Mcno}</td>
                      <td className="border px-2 py-1">{d.AcceptQty}</td>
                      <td className="border px-2 py-1">{d.QcRejectQty}</td>
                      <td className="border px-2 py-1">{d.GrnBy}</td>
                      <td className="border px-2 py-1">{d.QcByName}</td>
                      <td className="border px-2 py-1">
                        <button
                          className="bg-blue-600 text-white text-xs px-2 py-1 rounded hover:bg-blue-700"
                          onClick={() => handleViewClick(d)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


  // Main render
  return (
    <div className="App p-5">
     <div className="bg-gradient-to-r from-blue-400 to-blue-500 text-white px-3 py-2 rounded-t-lg flex items-center h-[45px]">
  <button
    onClick={() => navigate("/dashboard")}
    className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-2 py-1 rounded-md font-medium flex items-center gap-1 text-xs shadow hover:shadow-md transition"
  >
    ⬅ Back
  </button>

  <h1 className="text-base font-semibold text-center flex-1">
    Inspection Report
  </h1>

  <div className="w-10" />
</div>

      {/* Filters card */}
    

      {/* TAB BAR */}
      <div className="mt-6">
        <div className="flex gap-3">
          {["Report", "Graph", "Details"].map(tab => (
            <button
              key={tab}
              className={`px-4 py-2 rounded-md text-sm transition-colors duration-200 ${activeTab === tab ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
            {activeTab === "Graph" && (
           <div className="flex flex-col gap-1 w-[220px] ml-auto">
              <tbale>
                <tr>
                  <td>
  <label className="text-gray-600 text-xs font-medium">Parameter</label>
  </td>
  <td>
  <select
    className="border border-gray-300 rounded-lg p-2 h-[34px] text-xs focus:ring-1 focus:ring-blue-500"
    value={selectedParameter}
    onChange={(e) => setSelectedParameter(e.target.value)}
  >
    <option value="">-- Select --</option>
    {uniqueParameters.map(p => (
      <option key={p} value={p}>{p}</option>
    ))}
  </select>
  </td>
  </tr>
  </tbale>
</div>
 )}
        </div>

        <div className="mt-5">
          {activeTab === "Report" && <ReportTab />}
          {activeTab === "Graph" && <GraphTab />}
          {activeTab === "Details" && (
            <DetailsTab
              VId={selectedSupplier?.value}
              Itemid={selectedPartNo?.value}
              fromDate={startDate}
              toDate={endDate}
              userName={sessionStorage.getItem("userName")}
              databaseKey={sessionStorage.getItem("databaseKey")}
              supplierName={selectedSupplier?.label}
              partNo={selectedPartNo?.label}
              table1Data={table1Data}
            />
          )}
        </div>
      </div>
    </div>
  );
}




